import { Select, SelectOptionType, TextInput } from '@octano/global-ui';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import SectionTitle from '../../../Text/SectionTitle';

const CHILE_LOWER_CASE = 'chile';

type ContactSectionProps = {
  prefix: string;
  fieldNames?: {
    addressStreet: string;
    addressNumber: string;
    addressExtra: string;
    country: string;
    region: string;
    commune: string;
    city: string;
    phone: string;
    cellPhone: string;
    email: string;
    customCity: string;
    location: string;
  };
  disabled?: boolean;
  hasEmail?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicContactNormal = ({
  prefix,
  fieldNames = {
    addressStreet: 'addressStreet',
    addressNumber: 'addressNumber',
    addressExtra: 'addressExtra',
    country: 'country',
    region: 'region',
    city: 'city',
    commune: 'commune',
    phone: 'phone',
    cellPhone: 'cellPhone',
    email: 'email',
    location: 'location',
    customCity: 'customCity',
  },
  disabled = false,
  hasEmail = false,
}: ContactSectionProps) => {
  const { control, watch } = useFormContext();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_FILE_PERSONAL_INFORMATION,
  );
  const selectedCountry = watch(fieldNames.country);
  const rut = watch('rut');
  const filteredCommunes: SelectOptionType[] = [];
  const filteredCities: SelectOptionType[] = [];

  const isChile = useMemo(() => {
    return String(selectedCountry?.label)
      .toLowerCase()
      .includes(CHILE_LOWER_CASE);
  }, [selectedCountry?.label]);

  return (
    <>
      <Row className="pt-5">
        <Col xs={12}>
          <SectionTitle text={t(`${prefix}.title`)} />
        </Col>
      </Row>
      <Row>
        <Col className="pb-3" xs={12} lg={8}>
          <TextInput
            name={fieldNames.addressStreet}
            label={t(`${prefix}.${fieldNames.addressStreet}`)}
            control={control}
            readOnly={disabled}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={fieldNames.addressNumber}
            label={t(`${prefix}.${fieldNames.addressNumber}`)}
            control={control}
            readOnly={disabled}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={fieldNames.addressExtra}
            label={t(`${prefix}.${fieldNames.addressExtra}`)}
            control={control}
            readOnly={disabled}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={fieldNames.country}
            label={t(`${prefix}.${fieldNames.country}`)}
            options={[]}
            control={control}
            disabled={disabled || Boolean(rut)}
          />
        </Col>
        {isChile ? (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.region}
                label={t(`${prefix}.${fieldNames.region}`)}
                options={[]}
                control={control}
                disabled={disabled}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.city}
                label={t(`${prefix}.${fieldNames.city}`)}
                options={filteredCities}
                control={control}
                shouldUnregister
                disabled={disabled}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.commune}
                label={t(`${prefix}.${fieldNames.commune}`)}
                options={filteredCommunes}
                control={control}
                shouldUnregister
                disabled={disabled}
              />
            </Col>
          </>
        ) : (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={fieldNames.location}
                label={t(`${prefix}.${fieldNames.location}`)}
                control={control}
                shouldUnregister
                readOnly={disabled}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={fieldNames.customCity}
                label={t(`${prefix}.${fieldNames.city}`)}
                control={control}
                shouldUnregister
                readOnly={disabled}
              />
            </Col>
          </>
        )}
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={fieldNames.phone}
            label={t(`${prefix}.${fieldNames.phone}`)}
            control={control}
            formatter="phone"
            placeholder={t(`common.placeholder.phone`)}
            readOnly={disabled}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={fieldNames.cellPhone}
            label={t(`${prefix}.${fieldNames.cellPhone}`)}
            control={control}
            formatter="phone"
            placeholder={t(`common.placeholder.cellphone`)}
            readOnly={disabled}
          />
        </Col>
        {hasEmail ? (
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.email}
              label={t(`${prefix}.${fieldNames.email}`)}
              control={control}
              disabled={disabled}
              readOnly={disabled}
            />
          </Col>
        ) : null}
      </Row>
    </>
  );
};

export default DynamicContactNormal;
